/**
 * t検定結果をダッシュボードの ResultCache 形式に変換するアダプター
 * Phase 1: 既存の unpairedTTest / pairedTTest を呼び出し、右ペイン表示用に変換する。
 * 将来 Tauri に差し替える際は、このアダプターの「計算呼び出し」部分だけを invoke に置き換える。
 */

import { unpairedTTest, pairedTTest, fTestEqualVariance, type TTestResult } from '../statistics/tTest';
import {
  mannWhitneyUTest,
  wilcoxonSignedRankTest,
  mannWhitneyToTTestShape,
  wilcoxonToTTestShape,
} from '../statistics/nonparametric';
import { leveneTest } from '../statistics/levene';
import { shapiroWilkTest } from '../statistics/shapiroWilk';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import type { AnalysisOptions, ResultCache, VariableMapping } from '../../stores/systems/keisuke/dashboardStore';
import { formatManuscriptFixedDigits, formatManuscriptPValue } from '../keisukeManuscriptFormat';
import { kt, KeisukeAdapterError, ktLng } from '../keisukeAdapterI18n';
import { formatPaperDocument } from '../keisukePaperFormat';

function formatNumber(value: number, digits = 4): string {
  return Number.isFinite(value) ? value.toFixed(digits) : '—';
}

const m2 = (v: number) => formatManuscriptFixedDigits(v, 2);
const m0 = (v: number) => formatManuscriptFixedDigits(v, 0);

function significancePhraseLng(
  lng: string,
  significant: boolean,
  marginal: boolean,
  context: 'groups' | 'paired' | 'means'
): string {
  if (context === 'means') {
    if (significant) return ktLng(lng, 'paper.ttest.significance.meansSignificant');
    if (marginal) return ktLng(lng, 'paper.ttest.significance.meansMarginal');
    return ktLng(lng, 'paper.ttest.significance.meansNotSignificant');
  }
  if (significant) {
    return context === 'paired'
      ? ktLng(lng, 'paper.ttest.significance.pairedSignificant')
      : ktLng(lng, 'paper.ttest.significance.groupsSignificant');
  }
  if (marginal) {
    return context === 'paired'
      ? ktLng(lng, 'paper.ttest.significance.pairedMarginal')
      : ktLng(lng, 'paper.ttest.significance.groupsMarginal');
  }
  return context === 'paired'
    ? ktLng(lng, 'paper.ttest.significance.pairedNotSignificant')
    : ktLng(lng, 'paper.ttest.significance.groupsNotSignificant');
}

/** 論文用 Methods + Results（lng 既定 en） */
export function buildTTestPaperText(
  result: TTestResult,
  testType: 'unpaired' | 'paired',
  groupNames?: [string, string],
  pairedColumnNames?: { before: string; after: string },
  lng = 'en'
): string {
  const g1 = groupNames?.[0] ?? 'Group 1';
  const g2 = groupNames?.[1] ?? 'Group 2';
  const significant = result.pValue < 0.05;
  const marginal = result.pValue >= 0.05 && result.pValue < 0.1;
  const pStr = formatManuscriptPValue(result.pValue);
  const ci = result.confidenceInterval;
  const zVal = m2(result.t);

  if (testType === 'paired' && result.method === 'wilcoxon') {
    const beforeCol = pairedColumnNames?.before ?? 'Before';
    const afterCol = pairedColumnNames?.after ?? 'After';
    const methods = ktLng(lng, 'paper.ttest.pairedWilcoxon.methods', {
      beforeCol,
      afterCol,
      n: result.n1,
    });
    const groupSummary = ktLng(lng, 'paper.ttest.pairedWilcoxon.groupSummary', {
      beforeCol,
      afterCol,
      median1: m2(result.mean1),
      median2: m2(result.mean2),
      n1: result.n1,
      n2: result.n2,
    });
    const testSentence = ktLng(lng, 'paper.ttest.pairedWilcoxon.testSentence', {
      phrase: significancePhraseLng(lng, significant, marginal, 'paired'),
      z: zVal,
      p: pStr,
    });
    const results = `${groupSummary} ${testSentence} ${ktLng(lng, 'paper.notes.nonparamZ')}`;
    return formatPaperDocument(lng, methods, results);
  }

  if (testType === 'unpaired' && result.method === 'mann-whitney') {
    const methods = ktLng(lng, 'paper.ttest.mannWhitney.methods');
    const groupSummary = ktLng(lng, 'paper.ttest.mannWhitney.groupSummary', {
      g1,
      g2,
      median1: m2(result.mean1),
      median2: m2(result.mean2),
      n1: result.n1,
      n2: result.n2,
    });
    const testSentence = ktLng(lng, 'paper.ttest.mannWhitney.testSentence', {
      phrase: significancePhraseLng(lng, significant, marginal, 'groups'),
      z: zVal,
      p: pStr,
    });
    const results = `${groupSummary} ${testSentence} ${ktLng(lng, 'paper.notes.nonparamZ')}`;
    return formatPaperDocument(lng, methods, results);
  }

  if (testType === 'paired') {
    const beforeCol = pairedColumnNames?.before ?? 'Before';
    const afterCol = pairedColumnNames?.after ?? 'After';
    const zeroVariance = Number.isFinite(result.stdDev1) && result.stdDev1 === 0;
    const methods = ktLng(lng, 'paper.ttest.pairedStudent.methods', {
      beforeCol,
      afterCol,
      n: result.n1,
    });
    let results = '';
    if (zeroVariance) {
      results = ktLng(lng, 'paper.ttest.pairedStudent.zeroVarianceResults', {
        afterCol,
        beforeCol,
        meanDiff: formatNumber(result.mean2 - result.mean1),
        ciLower: formatNumber(ci?.lower ?? 0),
        ciUpper: formatNumber(ci?.upper ?? 0),
      });
    } else {
      results = ktLng(lng, 'paper.ttest.pairedStudent.results', {
        beforeCol,
        afterCol,
        mean1: m2(result.mean1),
        sd1: m2(result.stdDev1 ?? 0),
        mean2: m2(result.mean2),
        sd2: m2(result.stdDev2 ?? 0),
        n: result.n1,
        phrase: significancePhraseLng(lng, significant, marginal, 'paired'),
        df: m0(result.df ?? 0),
        t: m2(result.t),
        p: pStr,
        meanDiff: m2(result.mean2 - result.mean1),
        ciLower: m2(ci?.lower ?? 0),
        ciUpper: m2(ci?.upper ?? 0),
        roundingNote: ktLng(lng, 'paper.notes.manuscriptRounding'),
      });
    }
    return formatPaperDocument(lng, methods, results);
  }

  const methodsBlock =
    result.method === 'student'
      ? ktLng(lng, 'paper.ttest.unpairedStudent.methods')
      : ktLng(lng, 'paper.ttest.unpairedWelch.methods');
  const groupSummary = ktLng(lng, 'paper.ttest.unpaired.groupSummary', {
    g1,
    g2,
    mean1: m2(result.mean1),
    sd1: m2(result.stdDev1 ?? 0),
    n1: result.n1,
    mean2: m2(result.mean2),
    sd2: m2(result.stdDev2 ?? 0),
    n2: result.n2,
  });
  const testSentence = ktLng(lng, 'paper.ttest.unpaired.testSentence', {
    testName:
      result.method === 'student'
        ? ktLng(lng, 'paper.ttest.unpaired.studentLabel')
        : ktLng(lng, 'paper.ttest.unpaired.welchLabel'),
    phrase: significancePhraseLng(lng, significant, marginal, 'means'),
    df: m2(result.df ?? 0),
    t: m2(result.t),
    p: pStr,
  });
  const ciSentence = ktLng(lng, 'paper.ttest.unpaired.ciSentence', {
    g1,
    g2,
    meanDiff: m2(result.mean1 - result.mean2),
    ciLower: m2(ci?.lower ?? 0),
    ciUpper: m2(ci?.upper ?? 0),
  });
  const resultsBlock = `${groupSummary} ${testSentence} ${ciSentence}\n\n${ktLng(lng, 'paper.notes.manuscriptRounding')}`;
  return formatPaperDocument(lng, methodsBlock, resultsBlock);
}

/**
 * 統計詳細タブ冒頭の短い概要。数値は下の表に任せ、手法と解釈メモだけ書く。
 */
function buildTTestStatsDetailText(opts: {
  testType: 'unpaired' | 'paired';
  method: TTestResult['method'];
  groupA?: string;
  groupB?: string;
}): string {
  const { testType, method, groupA, groupB } = opts;
  const lines: string[] = [];

  if (testType === 'paired') {
    if (method === 'wilcoxon') {
      lines.push(kt('statsDetail.ttest.pairedWilcoxonIntro'));
    } else {
      lines.push(kt('statsDetail.ttest.pairedIntro'));
      lines.push(kt('statsDetail.ttest.pairedCiNote'));
    }
  } else if (method === 'mann-whitney') {
    lines.push(kt('statsDetail.ttest.mannWhitneyIntro'));
    lines.push(kt('statsDetail.ttest.medianNote'));
  } else if (method === 'wilcoxon') {
    lines.push(kt('statsDetail.ttest.pairedWilcoxonIntro'));
  } else {
    lines.push(
      kt(
        method === 'student'
          ? 'statsDetail.ttest.unpairedIntroStudent'
          : 'statsDetail.ttest.unpairedIntroWelch'
      )
    );
    if (groupA && groupB) {
      lines.push(kt('statsDetail.ttest.unpairedCiNote', { groupA, groupB }));
    } else {
      lines.push(kt('statsDetail.ttest.unpairedCiNoteGeneric'));
    }
  }

  return lines.join('\n');
}

function tTestResultToResultCache(
  result: TTestResult,
  testType: 'unpaired' | 'paired',
  groupNames?: [string, string],
  pairedColumnNames?: { before: string; after: string }
): ResultCache {
  const significant = result.pValue < 0.05;
  const tTestPairwiseRow =
    testType === 'unpaired' && groupNames
      ? {
          groupA: groupNames[0],
          groupB: groupNames[1],
          pValue: result.pValue,
          significant,
        }
      : testType === 'paired' && pairedColumnNames
        ? {
            groupA: pairedColumnNames.before,
            groupB: pairedColumnNames.after,
            pValue: result.pValue,
            significant,
          }
        : undefined;

  const isNonparametric =
    result.method === 'mann-whitney' || result.method === 'wilcoxon';

  /** ノンパラは z・中央値。偽の df／平均／SD／NaN CI を統計詳細に載せない */
  const stats: Record<string, unknown> = isNonparametric
    ? {
        z: Math.round(result.t * 1000) / 1000,
        pValue: result.pValue,
        method: result.method,
        median1: Math.round(result.mean1 * 1000) / 1000,
        median2: Math.round(result.mean2 * 1000) / 1000,
        n1: result.n1,
        n2: result.n2,
      }
    : {
        t: Math.round(result.t * 1000) / 1000,
        df:
          result.df == null
            ? undefined
            : Number.isInteger(result.df)
              ? result.df
              : Math.round(result.df * 100) / 100,
        pValue: result.pValue,
        method: result.method,
        mean1: Math.round(result.mean1 * 1000) / 1000,
        mean2: Math.round(result.mean2 * 1000) / 1000,
        stdDev1: Math.round((result.stdDev1 ?? 0) * 1000) / 1000,
        stdDev2: Math.round((result.stdDev2 ?? 0) * 1000) / 1000,
        n1: result.n1,
        n2: result.n2,
      };
  if (
    !isNonparametric &&
    result.confidenceInterval &&
    Number.isFinite(result.confidenceInterval.lower) &&
    Number.isFinite(result.confidenceInterval.upper)
  ) {
    stats['95%CI lower'] = Math.round(result.confidenceInterval.lower * 1000) / 1000;
    stats['95%CI upper'] = Math.round(result.confidenceInterval.upper * 1000) / 1000;
  }

  const paperText = buildTTestPaperText(result, testType, groupNames, pairedColumnNames);

  const labelA =
    testType === 'paired' ? pairedColumnNames?.before : groupNames?.[0];
  const labelB =
    testType === 'paired' ? pairedColumnNames?.after : groupNames?.[1];
  const statsDetailText = buildTTestStatsDetailText({
    testType,
    method: result.method,
    groupA: labelA,
    groupB: labelB,
  });

  const significancePairs =
    testType === 'unpaired' && significant
      ? [{ leftIndex: 0, rightIndex: 1, pValue: result.pValue }]
      : undefined;

  return {
    tTestPairwiseRow,
    stats,
    paperText,
    statsDetailText,
    significancePairs,
    updatedAt: Date.now(),
  };
}

/**
 * gridData と analysisOptions から t検定を実行し、ResultCache を返す。
 * マッピング不足・データ不足・2群でない場合はエラーを throw する。
 */
export function runTTestAdapter(
  gridData: DataRow[],
  options: AnalysisOptions
): ResultCache {
  const testType = options.testType ?? 'unpaired';
  const inferentialRaw = options.ttestInferential ?? 'parametric';
  const inferential =
    testType === 'paired'
      ? inferentialRaw === 'mann-whitney'
        ? 'wilcoxon'
        : inferentialRaw
      : inferentialRaw === 'wilcoxon'
        ? 'mann-whitney'
        : inferentialRaw;
  const mapping: VariableMapping = options.mapping ?? {};

  if (gridData.length === 0) {
    throw new KeisukeAdapterError('dataRequired');
  }

  if (testType === 'unpaired') {
    const groupColumn = mapping.groupColumn;
    const valueColumn = mapping.valueColumn;
    if (!groupColumn || !valueColumn) {
      throw new KeisukeAdapterError('groupAndValueColumnsRequired');
    }

    const groups: Record<string, number[]> = {};
    for (const row of gridData) {
      const group = String(row[groupColumn] ?? '').trim();
      const num = parseFiniteNumberFromCell(row[valueColumn]);
      if (num === null || !group) continue;
      if (!groups[group]) groups[group] = [];
      groups[group].push(num);
    }

    const groupNames = Object.keys(groups);
    if (groupNames.length !== 2) {
      throw new KeisukeAdapterError('twoGroupsRequired');
    }
    const g1 = groups[groupNames[0]];
    const g2 = groups[groupNames[1]];
    if (!g1.length || !g2.length) {
      throw new KeisukeAdapterError('invalidNumericData');
    }
    if (g1.length < 2 || g2.length < 2) {
      throw new KeisukeAdapterError('minTwoPerGroup');
    }

    let result: TTestResult;
    if (inferential === 'mann-whitney') {
      result = mannWhitneyToTTestShape(mannWhitneyUTest(g1, g2)) as TTestResult;
    } else {
      const equalVariance = options.equalVariance === true;
      result = unpairedTTest(g1, g2, equalVariance);
    }
    const cache = tTestResultToResultCache(result, 'unpaired', [groupNames[0], groupNames[1]]);
    /** 仮定チェックは parametric / ノンパラ共通の参考（手法選択の根拠・ヒント用） */
    const varianceTest = fTestEqualVariance(g1, g2);
    const levene = leveneTest([g1, g2], true);
    const normality: Record<string, { W: number; pValue: number }> = {};
    for (const name of groupNames) {
      try {
        const sw = shapiroWilkTest(groups[name]!);
        normality[name] = { W: sw.W, pValue: sw.pValue };
        cache.stats![`Shapiro–Wilk W (${name})`] = Math.round(sw.W * 10000) / 10000;
        cache.stats![`Shapiro–Wilk p (${name})`] = sw.pValue;
      } catch {
        /* n が小さすぎる等 */
      }
    }
    cache.stats!['Levene F'] = Math.round(levene.statistic * 1000) / 1000;
    cache.stats!['Levene p'] = levene.pValue;
    cache.stats!['F-test (variance) F'] = Math.round(varianceTest.f * 1000) / 1000;
    cache.stats!['F-test (variance) p'] = varianceTest.pValue;
    cache.rawResult = {
      result,
      groupNames: [groupNames[0], groupNames[1]],
      groups,
      varianceTest,
      levene,
      normality,
    };
    return cache;
  }

  // paired
  const beforeColumn = mapping.beforeColumn;
  const afterColumn = mapping.afterColumn;
  if (!beforeColumn || !afterColumn) {
    throw new KeisukeAdapterError('beforeAfterColumnsRequired');
  }

  const before: number[] = [];
  const after: number[] = [];
  for (const row of gridData) {
    const bNum = parseFiniteNumberFromCell(row[beforeColumn]);
    const aNum = parseFiniteNumberFromCell(row[afterColumn]);
    if (bNum === null || aNum === null) continue;
    before.push(bNum);
    after.push(aNum);
  }

  if (before.length === 0 || after.length === 0) {
    throw new KeisukeAdapterError('invalidNumericData');
  }
  if (before.length !== after.length) {
    throw new KeisukeAdapterError('beforeAfterCountMismatch');
  }
  if (before.length < 2) {
    throw new KeisukeAdapterError('minTwoDataPoints');
  }

  const result =
    inferential === 'wilcoxon'
      ? (wilcoxonToTTestShape(wilcoxonSignedRankTest(before, after), before, after) as TTestResult)
      : pairedTTest(before, after);
  const cache = tTestResultToResultCache(result, 'paired', undefined, {
    before: beforeColumn,
    after: afterColumn,
  });
  const diff = before.map((b, i) => after[i]! - b);
  try {
    const sw = shapiroWilkTest(diff);
    cache.stats!['Shapiro–Wilk W (diff)'] = Math.round(sw.W * 10000) / 10000;
    cache.stats!['Shapiro–Wilk p (diff)'] = sw.pValue;
    cache.rawResult = { result, before, after, normalityDiff: sw };
  } catch {
    cache.rawResult = { result, before, after };
  }
  return cache;
}
